import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import type { Item } from './canvasStore';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import { parseCanvasAction, executeCanvasAction } from '@/lib/partnrActionExecutor';

// AI usage tracking functions
const checkAndTrackAIUsage = async (userId: string): Promise<boolean> => {
  // Fetch current settings
  const { data: settings, error } = await supabase
    .from('user_settings')
    .select('ai_requests_used, ai_requests_limit, ai_notified_50, ai_notified_100')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !settings) {
    console.warn('Could not fetch user settings for AI tracking');
    return true; // Allow request if we can't check
  }

  if (settings.ai_requests_used >= settings.ai_requests_limit) {
    toast.error(`AI request limit reached (${settings.ai_requests_limit} requests). Contact support for more.`);
    return false;
  }

  return true;
};

const incrementAIUsage = async (userId: string): Promise<void> => {
  const { data: settings } = await supabase
    .from('user_settings')
    .select('ai_requests_used, ai_requests_limit, ai_notified_50, ai_notified_100')
    .eq('user_id', userId)
    .maybeSingle();

  if (!settings) return;

  const newUsage = settings.ai_requests_used + 1;
  const usagePercent = (newUsage / settings.ai_requests_limit) * 100;

  const updates: Record<string, unknown> = {
    ai_requests_used: newUsage,
  };

  if (usagePercent >= 50 && !settings.ai_notified_50) {
    toast.info(`You've used 50% of your Partnr AI requests (${newUsage} / ${settings.ai_requests_limit})`);
    updates.ai_notified_50 = true;
  }

  if (usagePercent >= 90 && !settings.ai_notified_100) {
    toast.warning(`You're almost out of AI requests (${usagePercent.toFixed(0)}% used)`);
    updates.ai_notified_100 = true;
  }

  await supabase
    .from('user_settings')
    .update(updates)
    .eq('user_id', userId);
};

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface PartnrChat {
  id: string;
  user_id: string;
  board_id: string;
  referenced_item_ids: string[];
  messages: ChatMessage[];
  title: string | null;
  created_at: string;
  updated_at: string;
  position?: { x: number; y: number };
  isMinimized?: boolean;
}

const MAX_PARTNR_SELECTIONS = 5;

interface PartnrState {
  isPartnrMode: boolean;
  partnrSelectedItemIds: Set<string>; // Separate selection for Partnr context
  activeChats: PartnrChat[];
  chatHistory: PartnrChat[];
  streamingChatId: string | null;
  isStreaming: boolean;
  isHistoryOpen: boolean;
  useBoardContextInNextMessage: boolean;
  
  // Canvas-native action state (separate from chat)
  canvasActionLoading: boolean;
  canvasActionSourceIds: string[];  // IDs of items being processed
  
  // Visual connection between source items and AI-generated result
  canvasActionConnection: {
    sourceItemIds: string[];
    resultItemId: string;
    createdAt: number; // timestamp for auto-dismiss
  } | null;
  
  // Search escalation context
  searchEscalationContext: {
    originalQuery: string;
    resultCount: number;
    resultItemIds: string[];  // IDs of items found in search
  } | null;
  
  setPartnrMode: (enabled: boolean) => void;
  togglePartnrItemSelection: (itemId: string) => boolean; // Returns false if limit reached
  clearPartnrSelection: () => void;
  toggleHistoryPanel: () => void;
  setCanvasActionLoading: (loading: boolean, sourceIds?: string[]) => void;
  setCanvasActionConnection: (connection: { sourceItemIds: string[]; resultItemId: string } | null) => void;
  clearCanvasActionConnection: () => void;
  setSearchEscalationContext: (context: { originalQuery: string; resultCount: number; resultItemIds: string[] } | null) => void;
  createChat: (boardId: string, referencedItems: Item[], initialMessage?: string) => Promise<PartnrChat | null>;
  sendMessage: (chatId: string, message: string) => Promise<void>;
  closeChat: (chatId: string) => void;
  reopenChat: (chat: PartnrChat) => void;
  updateChatPosition: (chatId: string, position: { x: number; y: number }) => void;
  toggleChatMinimized: (chatId: string) => void;
  fetchChatHistory: (boardId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
}

const streamResponse = async (
  chat: PartnrChat,
  referencedItems: Item[],
  set: (fn: (state: PartnrState) => Partial<PartnrState>) => void,
  get: () => PartnrState,
  searchContext?: { originalQuery: string; resultCount: number } | null,
  boardContext?: { summary: string | null; topics: string[]; itemCount: number } | null,
) => {
  const itemContext = referencedItems.map(item => ({
    id: item.id,
    type: item.item_type,
    content: item.content as Record<string, unknown>,
  }));

  console.log('[Partnr] Starting stream with messages:', chat.messages.length);
  console.log('[Partnr] Item context:', itemContext.length, 'items');
  console.log('[Partnr] Search context:', !!searchContext);

  try {
    const session = await supabase.auth.getSession();
    if (!session.data.session?.access_token) {
      throw new Error('You must be logged in to use Partnr');
    }

    // Use the agent loop: send boardId and let the edge function handle
    // all context gathering (vector search, graph traversal, spatial clustering)
    // server-side via tool routing.
    const requestBody: Record<string, unknown> = {
      messages: chat.messages,
      boardId: chat.board_id || undefined,
      useAgentLoop: true,
      // Still pass through explicit item context (user-selected items)
      itemContext: itemContext.length > 0 ? itemContext : undefined,
      // Pass search escalation context if present (one-time)
      searchContext: searchContext || undefined,
    };

    console.log('[Partnr] Sending agent loop request for board:', chat.board_id);

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partnr-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Partnr] Response error:', response.status, errorText);
      let errorMessage = 'AI request failed';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorMessage;
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }

    console.log('[Partnr] Response OK, starting stream');
    if (!response.body) throw new Error('No response body');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let assistantContent = '';
    let textBuffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line.startsWith(':') || line.trim() === '') continue;
        if (!line.startsWith('data: ')) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === '[DONE]') break;

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            assistantContent += content;

            // Check for canvas action in accumulated content
            const actionResult = parseCanvasAction(assistantContent);
            if (actionResult) {
              // Execute the canvas action
              executeCanvasAction(actionResult.canvasAction).catch(e => {
                console.error('[Partnr] Canvas action execution failed:', e);
                toast.error('Failed to create items on canvas. Please try again.');
              });

              // Strip the action from the chat text, keep confirmation + remaining text
              const confirmText = actionResult.canvasAction.confirmation || '';
              assistantContent = confirmText
                ? (confirmText + (actionResult.remainingText ? '\n\n' + actionResult.remainingText : ''))
                : actionResult.remainingText;
            }

            set((state) => ({
              activeChats: state.activeChats.map(c => {
                if (c.id !== chat.id) return c;
                const displayContent = assistantContent.replace(/<!--CANVAS_ACTION:[\s\S]*?-->/g, '').trim();
                const lastMsg = c.messages[c.messages.length - 1];
                if (lastMsg?.role === 'assistant') {
                  return {
                    ...c,
                    messages: c.messages.map((m, i) =>
                      i === c.messages.length - 1 ? { ...m, content: displayContent } : m
                    ),
                  };
                }
                return {
                  ...c,
                  messages: [...c.messages, { role: 'assistant', content: displayContent, timestamp: new Date().toISOString() }],
                };
              }),
            }));
          }
        } catch {
          textBuffer = line + '\n' + textBuffer;
          break;
        }
      }
    }

    const state = get();
    const finalChat = state.activeChats.find(c => c.id === chat.id);
    if (finalChat) {
      await supabase
        .from('partnr_chats')
        .update({ messages: finalChat.messages as unknown as Json })
        .eq('id', chat.id);
      
      set((state) => ({
        chatHistory: state.chatHistory.map(c => c.id === chat.id ? finalChat : c),
      }));
      
      // Increment AI usage after successful response
      const userId = session.data.session?.user?.id;
      if (userId) {
        await incrementAIUsage(userId);
      }
    }
  } catch (error) {
    console.error('Streaming error:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to get AI response');
  } finally {
    set(() => ({ streamingChatId: null, isStreaming: false }));
  }
};

export const usePartnrStore = create<PartnrState>((set, get) => ({
  isPartnrMode: false,
  partnrSelectedItemIds: new Set<string>(),
  activeChats: [],
  chatHistory: [],
  streamingChatId: null,
  isStreaming: false,
  isHistoryOpen: false,
  useBoardContextInNextMessage: true,
  canvasActionLoading: false,
  canvasActionSourceIds: [],
  canvasActionConnection: null,
  searchEscalationContext: null,

  setPartnrMode: (enabled) => set({
    isPartnrMode: enabled,
    // Clear Partnr selection and search context when disabling
    partnrSelectedItemIds: enabled ? get().partnrSelectedItemIds : new Set<string>(),
    searchEscalationContext: enabled ? get().searchEscalationContext : null,
  }),
  
  togglePartnrItemSelection: (itemId) => {
    const state = get();
    const newSelection = new Set(state.partnrSelectedItemIds);
    
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
      set({ partnrSelectedItemIds: newSelection });
      return true;
    } else {
      if (newSelection.size >= MAX_PARTNR_SELECTIONS) {
        toast.error(`Maximum ${MAX_PARTNR_SELECTIONS} items can be referenced`);
        return false;
      }
      newSelection.add(itemId);
      set({ partnrSelectedItemIds: newSelection });
      return true;
    }
  },
  
  clearPartnrSelection: () => set({ partnrSelectedItemIds: new Set<string>() }),
  
  toggleHistoryPanel: () => set((state) => ({ isHistoryOpen: !state.isHistoryOpen })),
  
  setCanvasActionLoading: (loading, sourceIds) => set({ 
    canvasActionLoading: loading, 
    canvasActionSourceIds: sourceIds || [],
  }),
  
  setCanvasActionConnection: (connection) => set({
    canvasActionConnection: connection ? { ...connection, createdAt: Date.now() } : null,
  }),
  
  clearCanvasActionConnection: () => set({ canvasActionConnection: null }),
  
  setSearchEscalationContext: (context) => set({ searchEscalationContext: context }),

  createChat: async (boardId, referencedItems, initialMessage) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in to use Partnr.');
      return null;
    }

    // Only check AI usage if we're actually going to send a message
    if (initialMessage) {
      const canProceed = await checkAndTrackAIUsage(user.id);
      if (!canProceed) {
        return null;
      }
    }

    // Close any existing active chats before creating a new one (single chat window)
    const state = get();
    if (state.activeChats.length > 0) {
      set({ activeChats: [] });
    }

    const referencedItemIds = referencedItems.map(item => item.id);
    const messages: ChatMessage[] = initialMessage
      ? [{ role: 'user', content: initialMessage, timestamp: new Date().toISOString() }]
      : [];
    const title = initialMessage
      ? initialMessage.substring(0, 50) + (initialMessage.length > 50 ? '...' : '')
      : 'New chat';

    const { data, error } = await supabase
      .from('partnr_chats')
      .insert({
        user_id: user.id,
        board_id: boardId,
        referenced_item_ids: referencedItemIds,
        messages: messages as unknown as Json,
        title,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating chat:', error);
      toast.error('Failed to start chat. Please try again.');
      return null;
    }

    const newChat: PartnrChat = {
      ...data,
      messages: data.messages as unknown as ChatMessage[],
      position: { x: window.innerWidth / 2 - 175, y: window.innerHeight / 2 - 200 },
    };

    // If no initial message, just open the chat window without streaming
    if (!initialMessage) {
      set((state) => ({
        activeChats: [newChat],
        chatHistory: [newChat, ...state.chatHistory],
      }));
      return newChat;
    }

    set((state) => ({
      activeChats: [newChat], // Only one chat at a time
      chatHistory: [newChat, ...state.chatHistory],
      streamingChatId: newChat.id,
      isStreaming: true,
    }));

    const escalationContext = get().searchEscalationContext;

    // Context gathering (vector search, graph, spatial, board profile) is now
    // handled server-side via the agent loop. We only pass selected items and
    // search escalation context.
    streamResponse(newChat, referencedItems, set, get, escalationContext);

    // Clear the escalation context after use (one-time context)
    set(() => ({ searchEscalationContext: null }));
    return newChat;
  },

  sendMessage: async (chatId, message) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Check AI usage limit before sending message
    const canProceed = await checkAndTrackAIUsage(user.id);
    if (!canProceed) return;

    const state = get();
    const chat = state.activeChats.find(c => c.id === chatId);
    if (!chat) return;

    const newMessage: ChatMessage = { role: 'user', content: message, timestamp: new Date().toISOString() };
    
    set((state) => ({
      activeChats: state.activeChats.map(c =>
        c.id === chatId ? { ...c, messages: [...c.messages, newMessage] } : c
      ),
      streamingChatId: chatId,
      isStreaming: true,
    }));

    const updatedState = get();
    const updatedChat = updatedState.activeChats.find(c => c.id === chatId);
    if (!updatedChat) return;

    let referencedItems: Item[] = [];
    if (chat.referenced_item_ids.length > 0) {
      const { data } = await supabase
        .from('items')
        .select('*')
        .in('id', chat.referenced_item_ids);
      if (data) referencedItems = data as Item[];
    }

    await streamResponse(updatedChat, referencedItems, set, get);
  },

  closeChat: (chatId) => set((state) => ({
    activeChats: state.activeChats.filter(c => c.id !== chatId),
    searchEscalationContext: null,
  })),

  reopenChat: (chat) => {
    const state = get();
    
    // Close any existing active chats before reopening (single chat window)
    set({
      activeChats: [{ ...chat, position: { x: window.innerWidth / 2 - 175, y: window.innerHeight / 2 - 200 } }],
      isHistoryOpen: false,
    });
  },

  updateChatPosition: (chatId, position) => set((state) => ({
    activeChats: state.activeChats.map(c => c.id === chatId ? { ...c, position } : c),
  })),

  toggleChatMinimized: (chatId) => set((state) => ({
    activeChats: state.activeChats.map(c => c.id === chatId ? { ...c, isMinimized: !c.isMinimized } : c),
  })),

  fetchChatHistory: async (boardId) => {
    const { data, error } = await supabase
      .from('partnr_chats')
      .select('*')
      .eq('board_id', boardId)
      .order('updated_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching chat history:', error);
      return;
    }

    const chats: PartnrChat[] = (data || []).map(chat => ({
      ...chat,
      messages: chat.messages as unknown as ChatMessage[],
    }));

    set({ chatHistory: chats });
  },

  deleteChat: async (chatId) => {
    const { error } = await supabase.from('partnr_chats').delete().eq('id', chatId);
    if (error) {
      console.error('Error deleting chat:', error);
      toast.error('Failed to delete chat');
      return;
    }

    set((state) => ({
      activeChats: state.activeChats.filter(c => c.id !== chatId),
      chatHistory: state.chatHistory.filter(c => c.id !== chatId),
    }));
    toast.success('Chat deleted');
  },
}));
